import type { FC } from 'react';
import { useState, useCallback, memo, useMemo } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Typography,
  Box,
  styled,
} from '@mui/material';
import { Label } from '@pflegenavi/web-components';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useTranslation } from 'react-i18next';
import {
  useFormatting,
  useFormatDate,
} from '@pflegenavi/frontend/localization';
import type { EmployeeCardWithDates } from '@pflegenavi/frontend/api-nursing-home';
import {
  getStatusColor,
  getContextualLimitLabel,
  getNextResetDate,
  isCardTerminated,
} from '@pflegenavi/frontend/api-nursing-home';
import { EmployeeCardDetailModalView } from './EmployeeCardDetailModalView';

export interface EmployeeCardOverviewViewProps {
  card: EmployeeCardWithDates;
}

export const EmployeeCardOverviewView: FC<EmployeeCardOverviewViewProps> = memo(
  ({ card }) => {
    const { t } = useTranslation();
    const { fCurrencyCents } = useFormatting();
    const fDate = useFormatDate();

    const [modalOpen, setModalOpen] = useState(false);

    const statusColor = useMemo(
      () => getStatusColor(card.card_status),
      [card.card_status]
    );

    const nextResetDate = useMemo(
      () => getNextResetDate(card.limit_renew_frequency),
      [card.limit_renew_frequency]
    );

    const handleCardClick = useCallback(() => {
      setModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
      setModalOpen(false);
    }, []);

    return (
      <>
        <StyledCard onClick={handleCardClick}>
          <CardContent>
            <Stack spacing={2}>
              {/* Header with icon, type and status */}
              <Stack
                direction="row"
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <CreditCardIcon color="primary" />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t(`myCards.cardType.${card.card_type}`)}
                  </Typography>
                </Stack>
                <Label variant="filled" color={statusColor}>
                  {t(`myCards.status.${card.card_status}`)}
                </Label>
              </Stack>

              {/* Last 4 digits */}
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                  }}
                >
                  •••• {card.last_four_digits}
                </Typography>
              </Box>

              {/* Transaction limit and available balance */}
              <Stack spacing={0.5}>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                    }}
                  >
                    {t('myCards.availableBalance')}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: 'success.main',
                    }}
                  >
                    {fCurrencyCents(card.available_balance)}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t(getContextualLimitLabel(card.limit_renew_frequency))}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {fCurrencyCents(card.total_limit)}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t('myCards.transactionLimit')}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {fCurrencyCents(card.transaction_limit)}
                  </Typography>
                </Stack>
              </Stack>

              {/* Next reset date if available and card not terminated */}
              {nextResetDate && !isCardTerminated(card.card_status) && (
                <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {t('myCards.nextReset')}:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                      }}
                    >
                      {fDate(nextResetDate, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Stack>
          </CardContent>
        </StyledCard>
        <EmployeeCardDetailModalView
          card={card}
          open={modalOpen}
          onClose={handleCloseModal}
        />
      </>
    );
  }
);

const StyledCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4],
      transform: 'translateY(-2px)',
    },
  })
);
